import { CellClassParams } from "ag-grid-community";
import {
    IGridItem,
    Item,
    UPC_DELIMITER,
    ValueTypes,
    ValidationModes,
    ValidationLevel,
} from "constants/product-collection";
import { isValidNumber } from "lib/number";
import { ISchemaKey } from "../product-collection";
import { get } from "lodash";
import memoizeOne from "memoize-one";
import { List } from "immutable";

import { amphibiousGetIn } from "helpers/objUtils";
import { Message } from "components/product-collection/product-collection-log/activity";
export const MODE_FIELD = "mode";

export enum Operator {
    And = "and",
    Or = "or",
    Eq = "eq",
    Neq = "neq",
    In = "in",
    Gt = "gt",
    Gte = "gte",
    Lt = "lt",
    Lte = "lte",
    NotContains = "not_contains",
    MaxLength = "max_length",
    IsNumber = "is_number",
    IsAlpha = "is_alpha",
}

export const validateScalarArray = (arr, singleRule, operator, valueType) =>
    arr.every((value) => validateByRuleAndValue(singleRule, operator, value, valueType));
export const validateDelimitedValue = (
    rule: nuorder.collection.item_display_property.Rule,
    fieldName: string,
    rowData: any | undefined,
    delimiter = UPC_DELIMITER,
) => {
    const operator = getOperatorByRule(rule);
    const singleRule = rule[operator];
    const fieldValue = rowData?.fields?.[fieldName]?.string_value ?? "";

    return validateScalarArray(fieldValue.split(delimiter), singleRule, operator, ValueTypes.STRING);
};
function convertToNumberIfPossible(value: any): any {
    const tryConvertToNum = Number(value);
    if (typeof value === "string" && !isNaN(tryConvertToNum)) {
        return tryConvertToNum;
    }
    return value;
}

const validateByRuleAndValue = (singleRule, operator, value, valueType) => {
    let expectedValue: Nullish<string | number | boolean> = singleRule.value;
    let emptyRuleValue = false;
    // Empty value
    if (singleRule.value === undefined || singleRule.value === null || singleRule.value.trim() === "") {
        emptyRuleValue = true;
    }

    if (![Operator.NotContains, Operator.MaxLength, Operator.IsNumber, Operator.IsAlpha].includes(operator)) {
        switch (valueType) {
            // adding isNaN check just to ensure other type is not forecefully typecasted to number
            case ValueTypes.NUMBER:
                value = convertToNumberIfPossible(value);
                expectedValue = convertToNumberIfPossible(expectedValue);
                break;
            case ValueTypes.BOOL:
                value = value === "true";
                expectedValue = expectedValue === "true";
                break;
            default:
                break;
        }
    } else {
        value = value ?? "";
    }
    switch (operator) {
        case Operator.Neq:
            if (emptyRuleValue) {
                // Doesn't expect empty value
                return !!value;
            }

            return value != expectedValue;
        case Operator.Eq:
            if (emptyRuleValue) {
                // Expect empty value
                return !value;
            }

            return value == expectedValue;
        case Operator.Gt:
            return value > expectedValue;
        case Operator.Gte:
            return value >= expectedValue;
        case Operator.Lt:
            return value < expectedValue;
        case Operator.Lte:
            return value <= expectedValue;
        case Operator.NotContains:
            return !value.includes(expectedValue);
        case Operator.MaxLength:
            return value.length <= expectedValue;
        case Operator.IsNumber:
            return isValidNumber(value);
        case Operator.IsAlpha:
            return ALPHA_REGEX.test(value);
        default:
            return false;
    }
};

// Validate based on provided rule.
// Returns `false` if not satisfied, `true` otherwise.
export const ALPHA_REGEX = /^[a-z0-9\s\.]*$/i;

// This function returns a date string if the conversion is possible else returns blank string
// {  dateValue object example :
//     "date_value": {
//         "year": 2023,
//         "month": 9,
//         "day": 12
//     }
// }
function convertToDateStr(fieldValue): string {
    const dateValue = fieldValue?.date_value;

    if (dateValue) {
        try {
            const dateObj = new Date(dateValue?.year, dateValue?.month - 1, dateValue?.day);
            const dateStr = dateObj.toDateString();
            return dateStr;
        } catch (error) {
            return "";
        }
    } else {
        return "";
    }
}

export const validateRule = (
    rule: nuorder.collection.item_display_property.Rule,
    fieldName: string,
    rowData: any | undefined,
    valueType: ValueTypes = ValueTypes.STRING,
): boolean => {
    const operator = getOperatorByRule(rule);
    if (![Operator.And, Operator.Or].includes(operator)) {
        const singleRule = rule[operator];
        // `singleRule.field` will reference to other field
        const fieldToValidate = singleRule.field || fieldName;

        const value = rowData?.fields?.[fieldToValidate]?.date_value
            ? convertToDateStr(rowData?.fields?.[fieldToValidate])
            : rowData?.fields?.[fieldToValidate]?.string_value;

        return validateByRuleAndValue(singleRule, operator, value, valueType);
    }

    const rules = rule[operator].rules || [];
    if (!rules.length) {
        return false;
    }

    return operator === Operator.And
        ? rules.every((_rule) => validateRule(_rule, fieldName, rowData, valueType))
        : operator === Operator.Or
          ? rules.some((_rule) => validateRule(_rule, fieldName, rowData, valueType))
          : false;
};

export const getOperatorByRule = (rule: nuorder.collection.item_display_property.Rule): Operator => {
    return Object.keys(rule)[0] as Operator;
};

export const getOperatorByCondition = (condition: nuorder.collection.item_display_property.Condition): Operator => {
    return Object.keys(condition)[0] as Operator;
};

const filterValidationsOnCondition = (
    fieldValidations: nuorder.collection.item_display_property.FieldValidation[],
    mode,
    value,
) =>
    fieldValidations.filter((validation) => {
        const eq = (validation.condition as nuorder.collection.item_display_property.OperandEq)?.eq;
        return eq && eq.field === mode && eq.value === value;
    });

export const validationsCellClassFactory = (
    fieldValidations: nuorder.collection.item_display_property.FieldValidation[] = [],
    schemaKey: ISchemaKey,
    editable: boolean = true,
) => {
    return (params: CellClassParams) => {
        if (fieldValidations.length === 0 || !params.data || params.node.group || params.node.rowPinned) {
            return false;
        }
        const { activeValidationMode, itemsToBeValidated } = params.context;
        const conditionedFieldValidations = activeValidationMode
            ? filterValidationsOnCondition(fieldValidations, MODE_FIELD, activeValidationMode)
            : [];

        if (!conditionedFieldValidations.length && !editable) {
            return false;
        }

        const itemIdsToBeValidated = itemsToBeValidated?.map((element) => element.fields.id.string_value);
        const items = itemsToBeValidated
            ? itemsToBeValidated?.find((element) => element.fields.id.string_value === get(params.data, Item.FIELDS.ID))
            : params.data;

        const nonConditionedFieldValidations = fieldValidations.filter((validation) => !validation.condition);

        return !nonConditionedFieldValidations
            .concat(itemIdsToBeValidated?.has(get(params.data, Item.FIELDS.ID)) ? conditionedFieldValidations : [])
            .every((validation) => validateRule(validation.rule, schemaKey.field_name, items, schemaKey.value_type));
    };
};

const getValidationRules = memoizeOne((itemDisplayProperty?: List<Immutable.Map<string, any>>) => {
    const validations = [];
    itemDisplayProperty?.forEach((schemaKey) => {
        const field_validations = schemaKey.get("field_validations")?.toJS();
        if (field_validations) {
            validations.push({
                field: schemaKey.get("field_name"),
                valueType: schemaKey.get("value_type"),
                field_validations,
            });
        }
    });
    return validations;
});

export type ValidationResult = { errors: Message[]; warnings: Message[] };
export const validateRuleForItem = (
    validationRule: nuorder.collection.item_display_property.Rule,
    level: ValidationLevel,
    item: IGridItem,
    originalField: string,
    valueType: ValueTypes,
): ValidationResult => {
    const validationOutput = { errors: [], warnings: [] };
    const op = getOperatorByRule(validationRule);
    const validationResult = validateRule(validationRule, originalField, item, valueType);
    if (!validationResult) {
        const rule = validationRule[op];
        const field = rule.field || originalField;
        let value = rule.value ?? "";
        if ([Operator.And, Operator.Or].includes(op)) {
            value = JSON.stringify(validationRule);
        }
        (level === ValidationLevel.ERROR
            ? validationOutput.errors
            : level === ValidationLevel.WARN
              ? validationOutput.warnings
              : []
        ).push({
            value,
            field,
            rule: op,
        });
    }
    return validationOutput;
};

export type ValidationResultWithIdentifier = ValidationResult & { id: string };
export const validateItemsWithFieldValidations = (
    items: IGridItem[],
    itemDisplayProperty?: List<Immutable.Map<string, any>>,
    validationMode?: ValidationModes,
): ValidationResultWithIdentifier[] => {
    // We could return just the number of validated/errored items for the scope of this task.
    // But we will require this validation verbosity when designing the error log.
    const validations = getValidationRules(itemDisplayProperty);
    const validatedItems = [];
    if (validations.length) {
        items.forEach((item) => {
            const validationOutput = { id: amphibiousGetIn(item, Item.FIELDS.ID), errors: [], warnings: [] };
            validations.forEach((validationForField) =>
                filterValidationsOnCondition(validationForField.field_validations, MODE_FIELD, validationMode).forEach(
                    (validation) => {
                        const validationResult = validateRuleForItem(
                            validation.rule,
                            validation.level as ValidationLevel,
                            item,
                            validationForField.field,
                            validationForField.valueType,
                        );
                        validationOutput.errors = validationOutput.errors.concat(validationResult.errors);
                        validationOutput.warnings = validationOutput.warnings.concat(validationResult.warnings);
                    },
                ),
            );
            validatedItems.push(validationOutput);
        });
    }

    return validatedItems;
};
